import { isVenueCode, normalizeVenueCode } from '@gc/shared';

/**
 * The venue code this browser signs in to, remembered so a till types it
 * once (EclipseGG platform, owner 2026-10-19: "the website and the apps
 * remember the code, so it is typed once").
 *
 * Written from what the SERVER says, never from the field: `useAuth` stores
 * `/auth/me`'s `venue.slug` at sign-in and at every boot. So a typo that
 * failed a sign-in is never remembered; a till that was already signed in on
 * deploy day (the 30-day sliding cookie keeps most of them signed in) learns
 * its code without anyone typing it; and a code the platform renames is
 * re-learnt on the next boot, because tokens carry the venue id, not the code.
 * That last holds only while the browser is still signed in: a boot with no
 * session never reads `/auth/me`. A browser signed out at the rename keeps the
 * old code until a sign-in with the new one, typed after "Change venue" or
 * after a refusal opens the field (LoginPage), teaches it.
 *
 * The code is not a secret (spec §1: the apps keep it in plain preferences),
 * so `localStorage` is the right home. Every access is wrapped, like
 * `gc.language` and `gc.staff.nav.wide`: storage throws in private windows and
 * locked-down kiosk browsers, and a convenience must never be the thing that
 * breaks the sign-in page. A stored value that is not a valid code (edited by
 * hand, or written by some later format) reads as nothing — prefilling a code
 * the server can only refuse would lock the field on a wrong answer.
 */
export const VENUE_CODE_STORAGE_KEY = 'gc.staff.venueCode';

/** The remembered code, normalised and valid (`isVenueCode`), else null. Never throws. */
export function readRememberedVenueCode(): string | null {
  try {
    const stored = localStorage.getItem(VENUE_CODE_STORAGE_KEY);
    if (stored === null) return null;
    const code = normalizeVenueCode(stored);
    return isVenueCode(code) ? code : null;
  } catch {
    return null;
  }
}

/**
 * Remembers `code` after `normalizeVenueCode`; an invalid one is ignored, and
 * never replaces a good one already stored. Never throws.
 */
export function rememberVenueCode(code: string): void {
  try {
    const normalized = normalizeVenueCode(code);
    if (!isVenueCode(normalized)) return;
    localStorage.setItem(VENUE_CODE_STORAGE_KEY, normalized);
  } catch {
    // Storage refused (private mode, quota): the till types its code next time.
  }
}

/**
 * Drops the remembered code. Never throws. The sign-in page's "Change venue"
 * does NOT call this — it clears the field only — so a stray tap followed by a
 * reload costs the till nothing; the next sign-in overwrites the code anyway.
 */
export function forgetVenueCode(): void {
  try {
    localStorage.removeItem(VENUE_CODE_STORAGE_KEY);
  } catch {
    // Storage refused: whatever it still holds only ever prefills the field.
  }
}
